import { useMemo } from 'react';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { Skeleton } from '@/lib/base/skeleton/skeleton';
import { useSlideoutStackContextOptional } from '@/lib/crm/contexts/SlideoutStackContext';
import { useEntityDisplayNames } from '@/lib/crm/hooks/useEntityDisplayNames';
import { cx } from '@/utils/cx';

import {
  buildTriggerContextItems,
  resolveTriggerShortcutContentState,
  type TriggerButtonDescriptor,
  type TriggerButtonTarget,
  type TriggerContextItem,
  type TriggerShortcutContentState,
} from './ActionOverview.helpers';
import { TriggerContextChip } from './TriggerContextChip';
import { TriggerContextPill } from './TriggerContextPill';

/**
 * How many context chips render expanded before the whole row collapses
 * into the single "Context N" pill. There is no partial overflow ("+N")
 * anymore: past the cap the entire set moves behind the pill's dropdown.
 * 4 matches the design mock's widest expanded row.
 */
const MAX_EXPANDED_CHIPS = 4;

// Keep assertNever local — do NOT import from pages/reasoning to avoid a
// components→pages import violation.
function assertNever(value: never): never {
  throw new Error(
    `Unhandled TriggerShortcutContentState: ${JSON.stringify(value)}`
  );
}

interface TriggerShortcutContentProps {
  state: TriggerShortcutContentState;
  /** Needed inside the loading case to pick the skeleton shape. */
  compact: boolean;
  items: TriggerContextItem[];
  onActivate: (target: TriggerButtonTarget) => void;
}

function TriggerShortcutContent({
  state,
  compact,
  items,
  onActivate,
}: TriggerShortcutContentProps) {
  switch (state.kind) {
    case 'loading':
      return (
        <span aria-hidden className='inline-flex items-center gap-1'>
          {compact ? (
            <Skeleton
              variant='rectangular'
              width='5.5rem'
              height='1.375rem'
              className='rounded-full'
            />
          ) : (
            <>
              <Skeleton
                variant='rectangular'
                width='6rem'
                height='1.25rem'
                className='rounded'
              />
              <Skeleton
                variant='rectangular'
                width='5rem'
                height='1.25rem'
                className='rounded'
              />
            </>
          )}
        </span>
      );
    case 'compact':
      return <TriggerContextPill items={items} onActivate={onActivate} />;
    case 'expanded':
      return (
        <>
          {items.map(item => (
            <TriggerContextChip
              key={item.key}
              item={item}
              onActivate={onActivate}
            />
          ))}
        </>
      );
    default:
      return assertNever(state);
  }
}

interface TriggerShortcutButtonsProps {
  isLoading: boolean;
  buttons: TriggerButtonDescriptor[];
  orgId: number | undefined;
  onActivate: (target: TriggerButtonTarget) => void;
  /**
   * Always render the collapsed "Context N" pill, whatever the viewport.
   * Set by narrow hosts whose width says nothing about the viewport (the
   * proposal sidebar / inline panel — 400–900px panels that can open on any
   * monitor).
   */
  forceCompact?: boolean;
  /** Viewport width at/above which chips render expanded. Default 'lg'. */
  inlineFrom?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
}

/**
 * The proposal header's trigger context — orchestrator. Resolves store-fresh
 * display names ONCE (batched) and reads the slideout stack, builds the
 * `TriggerContextItem` view models, then renders either the expanded row of
 * inline chips or the compact "Context N" dropdown pill:
 *
 * - compact when `forceCompact`, below `inlineFrom`, or when there are more
 *   than {@link MAX_EXPANDED_CHIPS} items;
 * - a pill/chip skeleton while the trigger fetch is in flight;
 * - nothing once loading is done with no items (unchanged contract).
 *
 * The leaves (TriggerContextChip / TriggerContextPill) are presentational.
 */
export function TriggerShortcutButtons({
  isLoading,
  buttons,
  orgId,
  onActivate,
  forceCompact = false,
  inlineFrom = 'lg',
  className,
}: TriggerShortcutButtonsProps) {
  const slideoutStack = useSlideoutStackContextOptional();

  const nameIds = useMemo(
    () => buttons.map(b => b.displayNameEntityId),
    [buttons]
  );
  const names = useEntityDisplayNames(nameIds, orgId);

  const items = useMemo(
    () => buildTriggerContextItems(buttons, names, slideoutStack?.stack),
    [buttons, names, slideoutStack?.stack]
  );

  // useBreakpoint re-renders on resize so the presentation tracks the live
  // viewport (the responsive-action-bar precedent).
  const isWide = useBreakpoint(inlineFrom);
  const compact = forceCompact || !isWide || items.length > MAX_EXPANDED_CHIPS;

  if (!isLoading && items.length === 0) {
    return null;
  }

  const contentState = resolveTriggerShortcutContentState(isLoading, compact);

  return (
    <div className={cx('flex items-center gap-1 flex-shrink-0', className)}>
      <TriggerShortcutContent
        state={contentState}
        compact={compact}
        items={items}
        onActivate={onActivate}
      />
    </div>
  );
}
